// src/pages/GatewayRegistration.js
import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, TextField, Button, Paper } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const GatewayRegistration = () => {
  const [formData, setFormData] = useState({
    serialNumber: '',
    imei: '',
    phoneNumber: '',
    bleAddress: '',
    macAddress: '',
    gpsCoordinates: '',
  });
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  // AbortController refs
  const checkFieldAbortControllerRef = useRef();
  const uniqueCheckAbortControllerRef = useRef();
  const submitAbortControllerRef = useRef();

  // Validation regexes
  const serialRegex = /^[A-Za-z0-9]{10}$/;                                   // same as water meter
  const bleRegex = /^([0-9A-Fa-f]{2}:){5}[0-9A-Fa-f]{2}$/;                   // for both BLE & MAC
  const phoneRegex = /^\d{10}$/;                                             // 10 digits
  const imeiRegex = /^[0-9]{15}$/;                                           // 15 digits
  const gpsRegex = /^-?\d+(?:\.\d+)?\s*,\s*-?\d+(?:\.\d+)?$/;                 // longitude,latitude

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => {
      const { [name]: _, ...rest } = prev;
      return rest;
    });
  };

  // Synchronous format validation for all fields
  const validateAll = () => {
    const newErrors = {};

    if (!serialRegex.test(formData.serialNumber)) {
      newErrors.serialNumber = 'Must be exactly 10 alphanumeric characters';
    }
    if (!imeiRegex.test(formData.imei)) {
      newErrors.imei = 'IMEI must be exactly 15 digits';
    }
    if (!phoneRegex.test(formData.phoneNumber)) {
      newErrors.phoneNumber = 'Phone Number must be exactly 10 digits';
    }
    if (!bleRegex.test(formData.bleAddress)) {
      newErrors.bleAddress = 'Must be in format XX:XX:XX:XX:XX:XX';
    }
    if (!bleRegex.test(formData.macAddress)) {
      newErrors.macAddress = 'Must be in format XX:XX:XX:XX:XX:XX';
    }
    if (formData.gpsCoordinates) {
      if (!gpsRegex.test(formData.gpsCoordinates)) {
        newErrors.gpsCoordinates = 'Invalid GPS format. Use longitude,latitude';
      }
    }

    return newErrors;
  };

  // Async uniqueness check for all non-GPS fields
  const checkAllUnique = async () => {
    const controller = new AbortController();
    uniqueCheckAbortControllerRef.current = controller;
    const signal = controller.signal;

    const newErrors = {};
    const fields = ['serialNumber', 'imei', 'phoneNumber', 'bleAddress', 'macAddress'];

    for (const field of fields) {
      const value = formData[field];
      if (!value || newErrors[field]) continue;

      try {
        const res = await api.get(`/api/proxy/fieldcheck/${value}`, {
          params: { fieldName: field },
          signal
        });
        if (res.data.exists) {
          newErrors[field] = `${field.charAt(0).toUpperCase() + field.slice(1)} already in use`;
        }
      } catch {
        newErrors[field] = `Error checking ${field}`;
      }
    }

    return newErrors;
  };

  // Per-field onBlur check (format + uniqueness)
  const checkField = async (field) => {
    const value = formData[field];
    if (!value) return;

    // format check
    if (field === 'serialNumber' && !serialRegex.test(value)) {
      setErrors(prev => ({ ...prev, serialNumber: 'Must be exactly 10 alphanumeric characters' }));
      return;
    }
    if (field === 'imei' && !imeiRegex.test(value)) {
      setErrors(prev => ({ ...prev, imei: 'IMEI must be exactly 15 digits' }));
      return;
    }
    if (field === 'phoneNumber' && !phoneRegex.test(value)) {
      setErrors(prev => ({ ...prev, phoneNumber: 'Phone Number must be exactly 10 digits' }));
      return;
    }
    if ((field === 'bleAddress' || field === 'macAddress') && !bleRegex.test(value)) {
      setErrors(prev => ({ ...prev, [field]: 'Must be in format XX:XX:XX:XX:XX:XX' }));
      return;
    }

    // uniqueness check
    try {
      const controller = new AbortController();
      checkFieldAbortControllerRef.current = controller;
      const signal = controller.signal;

      const res = await api.get(`/api/proxy/fieldcheck/${value}`, {
        params: { fieldName: field },
        signal
      });
      if (res.data.exists) {
        setErrors(prev => ({
          ...prev,
          [field]: `${field.charAt(0).toUpperCase() + field.slice(1)} already in use`
        }));
      }
    } catch {
      setErrors(prev => ({ ...prev, [field]: `Error checking ${field}` }));
    }
  };

  const checkGpsFormat = () => {
    if (formData.gpsCoordinates && !gpsRegex.test(formData.gpsCoordinates)) {
      setErrors(prev => ({ ...prev, gpsCoordinates: 'Invalid GPS format. Use longitude,latitude' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // 1) Format validation
    const formatErrors = validateAll();
    if (Object.keys(formatErrors).length > 0) {
      setErrors(formatErrors);
      return;
    }

    // 2) Uniqueness validation
    const uniqErrors = await checkAllUnique();
    if (Object.keys(uniqErrors).length > 0) {
      setErrors(uniqErrors);
      return;
    }

    // 3) Build payload
    const payload = {
      serialNumber: formData.serialNumber,
      imei: formData.imei,
      phoneNumber: formData.phoneNumber,
      bleAddress: formData.bleAddress,
      macAddress: formData.macAddress,
    };
    if (formData.gpsCoordinates) {
      const [lng, lat] = formData.gpsCoordinates.split(',').map(p => p.trim());
      payload.gpsCoordinates = [parseFloat(lng), parseFloat(lat)];
    }

    // 4) Submit
    try {
      const controller = new AbortController();
      submitAbortControllerRef.current = controller;
      const signal = controller.signal;

      const res = await api.post('/api/proxy/gateways/', payload, { signal });
      navigate(`/gateways/${res.data._id.$oid || res.data._id}`);
    } catch (err) {
      if (err.response?.status === 403) {
        setErrors({ form: 'Error registering gateway: Action denied' });
      } else {
        setErrors({ form: 'Error registering gateway' });
      }
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      checkFieldAbortControllerRef.current?.abort();
      uniqueCheckAbortControllerRef.current?.abort();
      submitAbortControllerRef.current?.abort();
    };
  }, []);

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <Paper sx={{ p: 4, width: 400 }}>
        <Typography variant="h5" align="center">
          Register Gateway
        </Typography>
        <form onSubmit={handleSubmit}>
          <TextField
            label="Serial Number"
            name="serialNumber"
            fullWidth margin="normal"
            value={formData.serialNumber}
            onChange={handleChange}
            onBlur={() => checkField('serialNumber')}
            error={!!errors.serialNumber}
            helperText={errors.serialNumber}
          />
          <TextField
            label="IMEI"
            name="imei"
            fullWidth margin="normal"
            value={formData.imei}
            onChange={handleChange}
            onBlur={() => checkField('imei')}
            error={!!errors.imei}
            helperText={errors.imei}
          />
          <TextField
            label="Phone Number"
            name="phoneNumber"
            fullWidth margin="normal"
            value={formData.phoneNumber}
            onChange={handleChange}
            onBlur={() => checkField('phoneNumber')}
            error={!!errors.phoneNumber}
            helperText={errors.phoneNumber}
          />
          <TextField
            label="BLE Address"
            name="bleAddress"
            fullWidth margin="normal"
            value={formData.bleAddress}
            onChange={handleChange}
            onBlur={() => checkField('bleAddress')}
            error={!!errors.bleAddress}
            helperText={errors.bleAddress}
          />
          <TextField
            label="MAC Address"
            name="macAddress"
            fullWidth margin="normal"
            value={formData.macAddress}
            onChange={handleChange}
            onBlur={() => checkField('macAddress')}
            error={!!errors.macAddress}
            helperText={errors.macAddress}
          />
          <TextField
            label="GPS Coordinates (optional, longitude, latitude)"
            name="gpsCoordinates"
            fullWidth margin="normal"
            value={formData.gpsCoordinates}
            onChange={handleChange}
            onBlur={checkGpsFormat}
            error={!!errors.gpsCoordinates}
            helperText={errors.gpsCoordinates || 'Format: longitude,latitude — e.g. 2.418300,6.365400'}
          />
          {errors.form && <Typography color="error" sx={{ mt: 1 }}>{errors.form}</Typography>}
          <Button type="submit" variant="contained" fullWidth sx={{ mt: 2 }}>
            Register
          </Button>
        </form>
      </Paper>
    </Box>
  );
};

export default GatewayRegistration;
